import { AlertTriangle } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";

/**
 * Error boundary — catches uncaught render errors and surfaces a
 * recovery affordance rather than blanking the page.
 *
 * Wraps the route Outlet at AppShell level + any sub-tree that
 * mounts third-party widgets (reactflow, recharts) where
 * a malformed payload could throw mid-render.
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface to the console for now; a later PR wires this to a
    // server-side error sink (Sentry / OpenTelemetry).
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
          <AlertTriangle className="h-8 w-8 text-state-failed-system" aria-hidden />
          <h2 className="font-display text-2xl font-semibold tracking-tight">
            Something went wrong here.
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {this.state.error.message || "An unexpected error broke the rendering of this surface."}
          </p>
          <Button onClick={this.reset} variant="outline" className="mt-2">
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
